import * as React from "react";
import { Topic, Category } from "../types";
import { slugify } from "../helpers";
import useApi from "@/hooks/useApi";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "@velaro/velaro-component-lib";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";

interface Props {
  onClose(): void;
  onSubmit(topic: Topic): void;
  categories?: Category[];
}

const INPUT_CLASS =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const LABEL_CLASS = "block text-sm font-medium text-slate-700 mb-1";

export default function TopicCreateModal(props: Props) {
  const [saving, setSaving] = React.useState(false);
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [categoryId, setCategoryId] = React.useState("");
  const api = useApi();

  async function handleSubmit() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const response = await api.admin.post("api/kb/topics", {
        name,
        slug,
        categoryId: categoryId || null
      });
      const json = await response.json();
      props.onSubmit(json);
    } catch (err) {
      console.error("Failed to create topic:", err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={true}>
      <ModalHeader title="New Topic" onClose={() => props.onClose()} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label htmlFor="topic-name" className={LABEL_CLASS}>Name</label>
              <input
                type="text"
                id="topic-name"
                className={INPUT_CLASS}
                autoFocus
                value={name}
                onChange={(e) => {
                  setName(e.currentTarget.value);
                  setSlug(slugify(e.currentTarget.value));
                }}
              />
            </div>
            <div>
              <label htmlFor="topic-slug" className={LABEL_CLASS}>Slug</label>
              <input
                type="text"
                id="topic-slug"
                className={INPUT_CLASS}
                value={slug}
                onChange={(e) => setSlug(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">Used in the URL for this topic</p>
            </div>
            {props.categories && props.categories.length > 0 && (
              <div>
                <label htmlFor="topic-category" className={LABEL_CLASS}>Category (optional)</label>
                <select
                  id="topic-category"
                  className={INPUT_CLASS}
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.currentTarget.value)}
                >
                  <option value="">Uncategorized</option>
                  {props.categories.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.Name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <SecondaryBlueButton
            label="Cancel"
            disabled={saving}
            onClick={() => props.onClose()}
          />
          <PrimaryBlueButton
            label={saving ? "Creating…" : "Create Topic"}
            disabled={saving || !name.trim()}
            onClick={() => handleSubmit()}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
}
